import { useEffect } from 'react';
import { generateJSON } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';

import { link, starterKit, tableExtensions, textAlign } from '@/components/editor/extensions';
import { Image } from '@/components/editor/nodes/image';
import { cn } from '@/lib/utils';
import { RichTextDiffExtension } from './rich-text-diff-extension';

const baseContentExtensions = [starterKit, link, Image, ...tableExtensions, textAlign];
const diffEditorExtensions = [...baseContentExtensions, RichTextDiffExtension];

interface RichTextDiffEditorProps {
  proposedHtml: string;
  originalHtml: string;
  className?: string;
}

export function RichTextDiffEditor({
  proposedHtml,
  originalHtml,
  className,
}: RichTextDiffEditorProps) {
  const editor = useEditor({
    extensions: diffEditorExtensions,
    content: proposedHtml,
    editable: false,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'prose max-w-none text-inherit [&_*]:text-inherit',
      },
    },
  });

  useEffect(() => {
    if (!editor) {
      return;
    }

    editor.commands.setContent(proposedHtml);
  }, [editor, proposedHtml]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    try {
      const comparisonDoc = generateJSON(originalHtml, baseContentExtensions);
      editor.commands.setComparisonContent(comparisonDoc);
    } catch {
      editor.commands.setComparisonContent(null);
    }
  }, [editor, originalHtml]);

  return (
    <div
      className={cn(className)}
      onClickCapture={(event) => {
        if (event.target instanceof Element && event.target.closest('a')) {
          event.preventDefault();
        }
      }}
    >
      <EditorContent editor={editor} />
    </div>
  );
}
